import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { ReactComponent as Spinner } from '@pflegia/ui/icons/spinner.svg';

const BASE_BUTTON_CLASSES =
  'sm:inline-block px-5 py-2.5 sm:py-3 text-sm sm:text-base text-center rounded-full font-medium hover:shadow-lg flex items-center justify-center';
const BUTTON_VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white',
  inverted: 'bg-white text-primary',
  alternative: 'bg-secondary text-white',
  invertedDecline: 'bg-white text-declined',
  secondary: 'border border-primary bg-transparent text-primary',
  apply: 'bg-primary text-white',
  decline: 'border border-declined text-declined',
  'decline-fill': 'bg-error text-white',
};
export const getButtonClassName = (variant?: ButtonVariant, className?: string): string =>
  twMerge(clsx(BASE_BUTTON_CLASSES, variant && BUTTON_VARIANT_CLASSES[variant], className));

// TODO: Remove and use React.ButtonHTMLAttributes['type']
export enum ButtonType {
  Default = 'button',
  Submit = 'submit',
  Reset = 'reset',
}

export type ButtonVariant = 'primary' | 'secondary' | 'apply' | 'decline' | 'decline-fill' | 'inverted' | 'invertedDecline' | 'alternative';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  className?: string;
  colorScheme?: ButtonVariant;
  disabled?: boolean;
  icon?: ReactNode;
  id?: string;
  isLoading?: boolean;
  label?: string | React.JSX.Element;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  removeVerticalPadding?: boolean;
  removeDisplayInline?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const Button: React.FC<ButtonProps> = ({
  children,
  className,
  colorScheme = 'primary',
  disabled,
  icon,
  id,
  isLoading,
  label,
  onClick,
  removeDisplayInline,
  removeVerticalPadding,
  type = 'button',
  ...props
}) => {
  const { t } = useTranslation('common');
  let baseButtonClassesReplace = BASE_BUTTON_CLASSES;
  if (removeDisplayInline) baseButtonClassesReplace = baseButtonClassesReplace.replace('sm:inline-block', '');
  if (removeVerticalPadding) baseButtonClassesReplace = baseButtonClassesReplace.replace('py-2.5 sm:py-3', '');
  return (
    <button
      {...props}
      className={twMerge(
        clsx(
          baseButtonClassesReplace,
          colorScheme && BUTTON_VARIANT_CLASSES[colorScheme],
          disabled && 'grayscale hover:shadow-none',
          className,
        ),
      )}
      disabled={disabled || isLoading}
      id={id}
      onClick={onClick}
      type={type}>
      {isLoading ? (
        <Spinner className='mx-auto h-6 w-6 animate-spin' title={t('loading')} />
      ) : (
        <div className='flex items-center justify-center space-x-2 sm:gap-x-2 sm:space-x-0'>
          {icon && <span>{icon}</span>}
          {(children || label) && (
            <span className={clsx('my-auto', removeVerticalPadding && 'text-sm font-medium')}>{children ?? label}</span>
          )}
        </div>
      )}
    </button>
  );
};

export default Button;
